// frontend/src/modules/reports/pages/workforce/LiveRosterPage.tsx
//
// Phase 4 (Reports Frontend), Task 3. Live roster — a point-in-time snapshot
// (no date range: uses ReportShell's `noDateRange` mode) of every employee's
// current status plus their monitoring agent's connectivity. Auto-refreshes
// every 30s via the monitoring module's `usePoll` (same pattern as
// LiveViewPanel/MonitoringPage) and shows an honest "as of <time>" line so
// the snapshot's staleness is never hidden.

import * as React from 'react';
import { Users, Clock3, AlertTriangle } from 'lucide-react';
import { ReportShell } from '../../components/ReportShell';
import { StatCard } from '../../../../design-system/components/StatCard';
import { DataTable } from '../../../../design-system/components/DataTable';
import { useApi } from '../../../../design-system/useApi';
import { usePoll } from '../../../monitoring/usePoll';
import { AgentStatusPill } from '../../../monitoring/components/AgentStatusPill';
import { reportUrl, downloadReportCsv } from '../../api';
import type { LiveRosterReport, LiveRosterStatus } from '../../types';
import { formatDateTime } from '../../../../design-system/format';
import { VISUAL, statusPill, type StatusTone } from '../../../../design-system/visual';

const STATUS_LABELS: Record<LiveRosterStatus, string> = {
  working: 'Working',
  on_break: 'On break',
  idle: 'Idle',
  late: 'Late',
  absent: 'Absent',
  off_today: 'Off today',
  on_leave: 'On leave',
  scheduled: 'Scheduled',
};

const STATUS_TONES: Record<LiveRosterStatus, StatusTone> = {
  working: 'success',
  on_break: 'info',
  idle: 'warning',
  late: 'destructive',
  absent: 'overdue',
  off_today: 'draft',
  on_leave: 'saffron',
  scheduled: 'muted',
};

const POLL_MS = 30_000;

export function LiveRosterPage() {
  const path = reportUrl('live-roster', {});
  const result = useApi<{ report: LiveRosterReport }>(path);
  const forbidden = result.status === 403;

  const [lastUpdatedAt, setLastUpdatedAt] = React.useState<string | null>(null);
  // Track the loading->loaded transition via a ref rather than depending on
  // `result.data` object identity directly — a fetch hook can legitimately
  // return a fresh object per render (e.g. under test mocks), and depending
  // on it would re-fire this effect every render, looping forever.
  const wasLoadingRef = React.useRef(true);
  React.useEffect(() => {
    if (wasLoadingRef.current && !result.loading) setLastUpdatedAt(new Date().toISOString());
    wasLoadingRef.current = result.loading;
  }, [result.loading]);

  usePoll(() => { result.reload(); }, POLL_MS, !forbidden);

  const report = result.data?.report ?? null;
  const rows = report?.rows ?? [];

  const counts = React.useMemo(() => {
    const c: Record<LiveRosterStatus, number> = {
      working: 0, on_break: 0, idle: 0, late: 0, absent: 0, off_today: 0, on_leave: 0, scheduled: 0,
    };
    for (const r of rows) c[r.status] += 1;
    return c;
  }, [rows]);

  return (
    <ReportShell
      eyebrow="Reports / Workforce"
      title="Live roster"
      description="Who's working right now, and their agent's connectivity — refreshes automatically every 30 seconds."
      noDateRange
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && result.loading && !report}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={!forbidden && !result.loading && !result.error && rows.length === 0}
      csv={{
        filename: `live-roster-${report?.date ?? 'today'}.csv`,
        onDownload: () => downloadReportCsv('live-roster', {}, `live-roster-${report?.date ?? 'today'}.csv`),
      }}
    >
      {report && (
        <>
          {lastUpdatedAt && (
            <p className="text-[12.5px] text-muted-foreground -mt-2">
              As of {formatDateTime(lastUpdatedAt)}
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Working" value={counts.working} icon={<Users />} />
            <StatCard label="Late" value={counts.late} icon={<Clock3 />} />
            <StatCard label="Absent" value={counts.absent} icon={<AlertTriangle />} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map((r) => (
              <div key={r.employee_id} className={[VISUAL.card, 'p-5'].join(' ')}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <span className="font-medium text-foreground text-[14px]">{r.employee_name}</span>
                  <span className={statusPill(STATUS_TONES[r.status])}>{STATUS_LABELS[r.status]}</span>
                </div>
                <div className="flex items-center justify-between gap-2 text-[12.5px] text-muted-foreground">
                  <span>
                    {r.expected_start && r.expected_end ? `${r.expected_start}–${r.expected_end}` : 'No shift today'}
                  </span>
                  <AgentStatusPill status={r.agent_status} lastHeartbeatAt={r.last_heartbeat_at} />
                </div>
              </div>
            ))}
          </div>

          <div className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <DataTable
              rows={rows}
              rowKey={(r) => r.employee_id}
              columns={[
                { key: 'employee', header: 'Employee', cell: (r) => <span className="font-medium text-foreground">{r.employee_name}</span> },
                { key: 'status', header: 'Status', cell: (r) => <span className={statusPill(STATUS_TONES[r.status])}>{STATUS_LABELS[r.status]}</span> },
                { key: 'shift', header: 'Shift', cell: (r) => (r.expected_start && r.expected_end ? `${r.expected_start}–${r.expected_end}` : '—') },
                { key: 'agent', header: 'Agent', cell: (r) => <AgentStatusPill status={r.agent_status} lastHeartbeatAt={r.last_heartbeat_at} /> },
                { key: 'pause', header: 'Pause', cell: (r) => (r.pause_kind ? (r.pause_kind === 'break' ? 'On break' : 'Idle') : '—') },
              ]}
            />
          </div>
        </>
      )}
    </ReportShell>
  );
}
